import { NgClass, NgStyle, NgTemplateOutlet } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { RadioButtonModule } from "primeng/radiobutton";
import { FormModel } from "src/app/form-builder/form.types";
import { isValidEmail } from "./fields.validators";
import { InputNumberModule } from "primeng/inputnumber";
import { NgxDropzoneModule } from "ngx-dropzone";
import { FileSizePipe } from "src/app/pipes/fileSize.pipe";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { FileUploadService } from "src/app/services/file-upload.service";
import { MessageService } from "primeng/api";
import * as codes from '../../../assets/Json/countryTeleCodes.json';
import { TextareaModule } from 'primeng/textarea';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FileSizeToBytesPipe } from "src/app/pipes/fileSizeToBytes.pipe";
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { Subject, takeUntil } from "rxjs";
import { SharedServicesService } from "src/app/services/shared-services.service";
import { FormBuilderService } from "src/app/services/form-builder.service";
import { ButtonModule } from "primeng/button";
import { DatePicker } from "primeng/datepicker";
import { Select } from "primeng/select";
import { TooltipModule } from "primeng/tooltip";
import { PhotoCaptureComponent } from "../photo-capture/photo-capture.component";

@Component({
    selector: 'app-fields-v2',
    templateUrl: './fields-v2.component.html',
    styleUrls: ['./fields-v2.component.css'],
    imports: [NgClass, CheckboxModule, FormsModule, MultiSelectModule, RadioButtonModule, InputTextModule, NgStyle, InputNumberModule, NgxDropzoneModule, FileSizePipe, ProgressSpinnerModule, TextareaModule, InputGroupModule, InputGroupAddonModule, NgTemplateOutlet, AutoCompleteModule, ButtonModule, DatePicker, Select, TooltipModule, PhotoCaptureComponent],
    providers: [FileSizeToBytesPipe]
})
export class FieldsV2Component implements OnInit, OnDestroy, OnChanges {
  private unsub$: Subject<void> = new Subject<void>();
  private institutionsSearch$: Subject<void> = new Subject<void>();
  @Input() removeFiles$: Subject<void> = new Subject<void>();

  private fileUploadService = inject(FileUploadService);
  private messageService = inject(MessageService);
  private fileSizeToBytesPipe = inject(FileSizeToBytesPipe)

  @Input() duplicateData: any = {};
  @Input() model!: FormModel;
  @Input() preview: boolean = true;
  @Input() companyId: any = {};
  @Input() emailVerified: boolean = false;
  @Input() eventId: string = "";
  @Input() editMode: boolean = false;
  @Input() preventDropdownAutoOpen: boolean = false;
  @Input() fromFeedback: boolean = false;
  @Input() photoCaptureMode: string | null = null;
  captureTab: 'camera' | 'upload' = 'camera';
  cameraDialogVisible = false;

  openCamera(): void {
    if (this.preview || (this.model.disabled ?? false) || !this.emailVerified) {
      return;
    }
    this.cameraDialogVisible = true;
  }

  private static readonly PHOTO_FIELD_NAME = 'photo';

  get isPhotoField(): boolean {
    return this.model?.fieldValue === FieldsV2Component.PHOTO_FIELD_NAME;
  }

  get showCaptureSwitcher(): boolean {
    return this.isPhotoField && !this.preview && !this.editMode && this.photoCaptureMode === 'both';
  }

  get showCameraInput(): boolean {
    if (!this.isPhotoField || this.preview || this.editMode) {
      return false;
    }
    return (
      this.photoCaptureMode === 'live' ||
      (this.photoCaptureMode === 'both' && this.captureTab === 'camera')
    );
  }

  setCaptureTab(tab: 'camera' | 'upload'): void {
    this.captureTab = tab;
  }

  onPhotoCaptured(file: File): void {
    this.onSelectfile({ addedFiles: [file] });
  }

  @Output() fieldDataEmitter = new EventEmitter<any>();
  @Output() collegeNameDataEmitter = new EventEmitter<any>();
  @Output() checkValidator = new EventEmitter<boolean>();
  @Output() enableEmailVerification = new EventEmitter<any>();
  @Output() enableMobileVerification = new EventEmitter<any>();
  @Input() verifyingEmail: boolean = false;

  acceptedFileTypes: string = "";
  acceptedFileFormats: string[] = [];
  uploadingFile: boolean = false;
  file: File[] = [];
  private fileUrlMap: Map<string, string> = new Map();
  fileBase64: string = "";

  belowAge: boolean = false;
  currentDate: Date | null = null;

  touched: boolean = false;

  countryCodeList: any = (codes as any).default;
  _countryCodeList: Array<any> = this.countryCodeList;
  selectedCode = { country: "India", countryCodes: ["91"], isoCode2: "IN", isoCode3: "IND", phLength: 10 };
  enableMobileVerify: boolean = false;
  enableMobile: boolean = false;
  mobileVerified: boolean = false;
  codeSearchTerm: string = '';
  percentageError: string | null = null;
  cgpaError: string | null = null;
  maxFileSize: number = 0;
  options: any[] = []

  @Input() markAsTouced: Subject<void> = new Subject<void>();

  constructor(
    private sharedService: SharedServicesService,
    private formBuilderService: FormBuilderService
  ) {}

  ngOnInit(): void {
    if (this.model.fieldType == 'file' || this.model.fieldType === "multi-file") {
      if(this.model.fileProperities?.file_format) {
        this.model.fileProperities.file_format = [...new Set(this.model.fileProperities.file_format)]
        this.acceptedFileFormats = this.model.fileProperities?.file_format;
      }
      if(this.model.fileProperities?.file_format?.includes("word")) {
        this.model.fileProperities?.file_format.push(...["doc", "docx"]);
        this.model.fileProperities.file_format = [...new Set(this.model.fileProperities.file_format)]
        this.acceptedFileFormats = this.model.fileProperities?.file_format?.filter((val) => val !== "word") || []
      }
      this.acceptedFileTypes = this.model.fileProperities?.file_format?.map((each: string) => '.' + each.toLowerCase()).join(',') ?? '';
      this.maxFileSize = this.fileSizeToBytesPipe.transform(this.model.fileProperities.file_max_size ?? '');
    }

    if (this.model.fieldName == 'If No - When can you start the internship/training?') {
      this.currentDate = new Date();
    }

    this.markAsTouced.pipe(takeUntil(this.unsub$)).subscribe((res) => {
      this.touched = true;
    })

    this.removeFiles$.pipe(takeUntil(this.unsub$)).subscribe((res) => {
      this.file = [];
      this.fileBase64 = "";
    })

    // Initialize options array with model field options
    this.updateOptions();
    if (this.editMode && (this.model.fieldType === 'file' || this.model.fieldType === 'multi-file')) {
      this.initFilesFromUrls();
    }
  }

  private initFilesFromUrls() {
    if (!this.model.answer?.length) return;
    const urls = this.model.answer.filter((val: any) => typeof val === 'string' && val.startsWith('http'));
    if (!urls.length) return;

    this.file = [];
    this.fileUrlMap.clear();

    for (const url of urls) {
      try {
        const urlPath = new URL(url).pathname;
        const segments = urlPath.split('/');
        let fileName = decodeURIComponent(segments[segments.length - 1]);
        if (fileName.includes('_')) {
          const parts = fileName.split('_');
          if (parts.length > 1) {
            const lastPart = parts[parts.length - 1];
            const extMatch = lastPart.match(/\.[a-zA-Z0-9]+$/);
            if (extMatch) {
              fileName = parts.slice(0, -1).join('_') + extMatch[0];
            }
          }
        }
        const placeholderFile = new File([''], fileName, { type: 'application/octet-stream' });
        this.file.push(placeholderFile);
        this.fileUrlMap.set(fileName, url);
      } catch (e) {
      }
    }
    if (this.file.length) {
      this.model.isValid = true;
    }
  }

  private updateOptions(): void {
    this.options = this.model.fieldOptions ? [...this.model.fieldOptions] : [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']?.currentValue) {
      this.updateOptions();
      if (this.editMode && (this.model.fieldType === 'file' || this.model.fieldType === 'multi-file')) {
        if (this.model.answer?.length && typeof this.model.answer[0] === 'string' && this.model.answer[0].startsWith('http') && !this.file.length) {
          this.initFilesFromUrls();
        }
      }
    }
    
    if(changes["model"]?.previousValue?.answer?.length === 0 && changes["model"]?.currentValue?.answer?.length === 2) {
      const countryCode = this._countryCodeList.find((country) => country.countryCodes[0] === changes["model"]?.currentValue?.answer[0]);
      if(countryCode) {
        this.selectedCode = countryCode;
      }
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();

    this.institutionsSearch$.next();
    this.institutionsSearch$.complete();
  }

  onpaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text/plain');
      const filteredText = pastedText.replace(/[^0-9.]/g, '');
      event.preventDefault();
      document.execCommand('insertText', false, filteredText);
    }
  }

  preventEAndMinus(event: KeyboardEvent): void {
    const invalidChars = ['-', 'e', 'E'];

    if (invalidChars.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  restrictNumber(event: any, len: number) {
    let e: any = event || window.event;
    let key = e.keyCode || e.which;

    if (key < 48 || key > 57 || event.target.value.length >= len) {
      if (e.preventDefault) e.preventDefault();
      e.returnValue = false;
    }
  }

  restrictCharacters(event: any, len: number) {
    let e: any = event || window.event;

    if (event.target.value.length >= len) {
      if (e.preventDefault) e.preventDefault();
      e.returnValue = false;
    }
  }

  verifyEmail() {
    this.enableEmailVerification.emit({ status: true, email: this.model.answer[0] });
  }


  verifyMobile() {
    const value = this.model.answer[0]
    let mobileNumber = this.selectedCode.countryCodes[0] + value;
    this.enableMobileVerification.emit({ status: true, mobile: mobileNumber, countryCode: this.selectedCode.countryCodes[0], number: value });
  }

  searchCode = (event: any) => {
    if ((event ?? '').length) {
      this._countryCodeList = this.countryCodeList.filter((val: any) => val.country.toLowerCase().includes(event.toLowerCase()));
    } else this._countryCodeList = this.countryCodeList;
  }

  onBlur() {
    this.touched = true;
  }

  onSelectfile(event: any, acl?: boolean) {
    let files: File[] = event.addedFiles;
    const indexToRemove: number[] = [];
    const removedFileNames: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const size = file.size;
      if (size > this.maxFileSize) {
        indexToRemove.push(i)
        removedFileNames.push(file.name);
      }
    }
    if (removedFileNames.length) {
      const removedList = removedFileNames.join(', ');
      const message = `Removed oversized file(s) (max ${this.model.fileProperities.file_max_size}): ${removedList}`;
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: message });
    }
    files = files.filter((val, index) => !indexToRemove.includes(index))

    if (files.length) {
      if (this.model.answer.length + files.length > 15 && this.model.fieldType === "multi-file") {
        this.messageService.add({ severity: 'warn', summary: 'Warning', detail: "You've reached the upload limit. You can only add up to 15 files." });
        return;
      }

      if (this.model.fieldType !== "multi-file") {
        if (this.file.length) this.file.shift();
      }
      this.handleUpload({ ...event, addedFiles: files }, acl)
    }
  }

  onRemovefile(fileToRemove?: File) {
    if (this.model.fieldType === "multi-file" && fileToRemove) {
      this.file = this.file.filter(f => f.name !== fileToRemove.name);
      if (fileToRemove.name) {
        this.fileUrlMap.delete(fileToRemove.name);
      }
    } else {
      this.file = [];
      this.fileUrlMap.clear();
    }

    if (this.model.fieldType === "multi-file") {
      const answerValues = this.file.map(f => this.fileUrlMap.get(f.name) || f.name);
      this.fieldDataEmitter.emit(answerValues);
    } else {
      this.fieldDataEmitter.emit("");
    }
    this.checkValidator.emit(this.model.isMandatory ? (this.file.length > 0) : true);
    this.fileBase64 = '';
  }

  viewFile() {
    const url = this.fileUrlMap.get(this.file[0]?.name) || this.model.answer?.[0];
    if (url && typeof url === 'string' && url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  viewFileAtIndex(index: number) {
    const f = this.file[index];
    if (!f) return;
    const url = this.fileUrlMap.get(f.name);
    if (url) {
      window.open(url, '_blank');
    }
  }

  handleUpload(event: any, acl?: boolean) {
    const files: File[] = this.model.fieldType === "multi-file" ? event.addedFiles : [event.addedFiles[0]];

    for (let file of files) {
      const fileName = this.generateFileName(file);
      const renamedFile = new File([file], fileName, {
        type: file.type,
        lastModified: file.lastModified,
      });
      file = renamedFile;
      const reader = new FileReader();
      reader.readAsDataURL(renamedFile);
      reader.onload = () => {
        this.fileUpload(renamedFile, fileName);
      };
    }

  }

  private generateFileName(file: any): string {
    let fileName = file.name.split(".");
    let fileExtension = fileName.pop();
    const timestamp = new Date().getTime();
    return `${fileName.join().replace(/\s/g, "").replace(/[^\w\s]/gi, '')}_${timestamp}.${fileExtension}`;
  }

  uploadFileToGCS(signedUrl: string, file: any, filePath: string, filename: string) {
    this.fileUploadService.uploadUsingSignedUrlGCS(signedUrl, file).subscribe({
      next: (response: any) => {
        if (response && response.status === "success") {
          this.fileBase64 = filename;
          this.fieldDataEmitter.emit(this.model.fieldType === "multi-file" ? [...(this.model.answer ? this.model.answer : []), this.fileBase64] : this.fileBase64);
          this.checkValidator.emit(true);
          this.uploadingFile = false;
          this.file.push(file)
        }
      },
      error: (error) => {
        console.error('Error uploading file to GCS:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error uploading file to GCS' });
        this.uploadingFile = false;
      }
    });
  }

  fileUpload(file: any, fileName: string) {
    this.uploadingFile = true;
    const filePath = `temp/${this.companyId}/${fileName}`;
    const payload = {
      file_path: filePath,
      bucket_type: 'private',
      origin: this.editMode ? 'registration_details_update' : 'candidate'
    };
    const isCandidate = !this.editMode;
    this.fileUploadService.getSignedUrlGCS(payload, isCandidate).subscribe({
      next: (response: any) => {
        if (response && response.body.signed_url) {
          this.uploadFileToGCS(response.body.signed_url, file, filePath, fileName);
        }
      },
      error: (error) => {
        console.log("Error while file upload: ", error)
        this.uploadingFile = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error getting signed URL' });
      }
    });
  }

  onEmailChange() {
    const required = this.model.isMandatory ?? false;
    let value = this.model.answer[0];
    if (value && typeof value === 'string') {
      value = value.toLowerCase();
      this.model.answer[0] = value;
    }

    const isValid = isValidEmail(value);
    const isValidField = (!required && isValid) || (required && isValid);

    this.model.isValid = isValidField;
    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(isValidField);
  }

  onInputChange() {
    const { isMandatory: required, fieldName, answer } = this.model;
    const value = answer[0];
    let isValid = true;

    switch (fieldName) {
      case 'Registration number':
        isValid = value.length < 20;
        break;
      case 'Aadhar Card Number':
        isValid = value.length === 12;
        break;
      case 'Pin Code':
        isValid = value.length === 6;
        break;
      default:
        if (required && !value) {
          isValid = false;
        }
        break;
    }

    if (isValid && fieldName.toLowerCase().includes('name')) {
      const nameRegex = /^[a-zA-Z ]*$/;
      isValid = nameRegex.test(value);
    }

    if(value.length > 250) {
      isValid = false;
    }

    if(this.sharedService.hasSingleInputViolation(value)) {
      isValid = false;
    }

    this.model.isValid = isValid;
    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(isValid);
  }

  onDropdownChange(autoComplete: AutoComplete) {
    const { answer } = this.model;
    const value = answer[0]

    if(!value && ["pg_college_name", "ug_college_name", "diploma_college_name"].includes(this.model.fieldValue)) {
      this.hasMoreInstitutions = true;
      this.institutionsPage = 1;
      this.institutionsSearchQuery = "";
      this.model.fieldOptions = [];
      this.updateOptions()
      this.getInstitutions();
    }

    this.model.isValid = this.model.isMandatory ? (value !== null && value !== undefined && value !== '') : true;
    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(this.model.isValid);

    if(["pg_college_name", "ug_college_name", "diploma_college_name"].includes(this.model.fieldValue)) {
      this.collegeNameDataEmitter.emit({ field: "college_name", value: "" });
      this.collegeNameDataEmitter.emit({ field: "college_state", value: "" });
      this.collegeNameDataEmitter.emit({ field: "college_city", value: "" });
    }

    setTimeout(() => {
      autoComplete.hide();
    }, 0);
  }

  onNumberChange() {
    const { isMandatory: required, answer } = this.model;
    const value = answer[0];
    let isValid = required ? !!value?.toString().length : true;

    if(["class10_cgpa", "ug_cgpa", "pg_cgpa", "diploma_cgpa", "class12_cgpa", "pg_cummulative_cgpa", "ug_cummulative_cgpa"].includes(this.model.fieldValue) && value !== null && value !== undefined && value != "") {
      this.cgpaError = "";
      const numericValue = Number(value);
      if(!isNaN(numericValue)) {
        if(numericValue > 10) {
          this.cgpaError = "CGPA cannot be more than 10";
          isValid = false;
        }
      }
    }

    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(isValid);
  }

  onPercentageChange() {
    const { isMandatory: required, answer } = this.model;
    const value = answer[0];    
    this.percentageError = null;    
    let isValid = required ? (value !== null && value !== undefined && value !== '') : true;    
    if (value !== null && value !== undefined && value !== '' && this.model.section_label?.toLowerCase() !== "custom fields") {
      const numericValue = Number(value);
      if (!isNaN(numericValue)) {
        if (numericValue < 10) {
          this.percentageError = 'Percentage must be more than 10';
          isValid = false;
        } else if (numericValue > 100) {
          this.percentageError = 'Percentage must be below 100';
          isValid = false;
        }
      }
    }
    
    this.model.isValid = isValid;
    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(isValid);
  }

  onRadioChange() {
    const { answer } = this.model;
    const value = answer[0];
    const isValid = this.model.isMandatory ? value !== undefined && value !== null && value !== '' : true;

    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(isValid);
  }

  onDateChanged() {
    this.belowAge = false;

    const value = this.model.answer[0];

    if (this.model.fieldValue === "date_of_birth") {
      const selectedDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - selectedDate.getFullYear();
      const monthDiff = today.getMonth() - selectedDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < selectedDate.getDate())) {
        age--;
      }

      if (age < 18) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Age must be at least 18 years' });
        this.checkValidator.emit(false);
        this.fieldDataEmitter.emit(value);
        this.belowAge = true;
      } else {
        this.belowAge = false;
        this.checkValidator.emit(true);
        this.fieldDataEmitter.emit(value);
      }
      return;
    }

    this.fieldDataEmitter.emit(value)
    this.checkValidator.emit(true)
  }

  onMobileChange() {
    if(this.model.answer?.length === 1) {
      if(this.model.answer[0]?.includes("-")) {
        const split = this.model.answer[0]?.split("-");
        this.model.answer[1] = split[split.length - 1];
      }
    }

    const value = this.model.answer[1]

    this.fieldDataEmitter.emit([this.selectedCode.countryCodes[0], value ? Number(value) : ""]);
    if(this.model.isMandatory && !value) {
      this.model.isValid = false;
      this.checkValidator.emit(false);
    } else if (value && value?.toString()?.length != this.selectedCode.phLength) {
      this.model.isValid = false;
      this.checkValidator.emit(false);
    } else {
      this.model.isValid = true;
      this.checkValidator.emit(true);
    }

    if (value?.length == this.selectedCode.phLength && !isNaN(Number(value))) {
      this.enableMobileVerify = true;
    } else {
      this.enableMobileVerify = false;
    }
  }

  onMultiDropdownChange() {
    let value = this.model.answer;
    if (value.length > 7) {
      this.model.answer = value.slice(0, 7)
      value = this.model.answer;
    }

    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(((this.model.isMandatory && value.length) || !this.model.isMandatory) ? true : false)
  }

  onTextareaChange() {
    const value = this.model.answer[0];
    let isValid = ((this.model.isMandatory && value.length) || !this.model.isMandatory) ? true : false

    if(this.sharedService.hasSingleInputViolation(value)) {
      isValid = false;
    }

    if(value?.length > 2000) {
      isValid = false;
    }

    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(isValid)
  }

  onCheckboxChange() {
    const value = this.model.answer;

    this.fieldDataEmitter.emit(value);
    this.checkValidator.emit(((this.model.isMandatory && value.length) || !this.model.isMandatory) ? true : false)
  }

  filterOption(event: AutoCompleteCompleteEvent) {
    if (["pg_college_name", "ug_college_name", "diploma_college_name"].includes(this.model.fieldValue)) {
      if(event.originalEvent.type !== "focus") {
        this.institutionsSearchQuery = event.query;
        if(this.institutionsSearchQuery.length >= 2 || !this.institutionsSearchQuery.length) {
          this.hasMoreInstitutions = true;
          this.model.fieldOptions = [];
          this.updateOptions()
          this.institutionsPage = 1;
          this.getInstitutions();
        }
      }
    } else {
      let filtered: any[] = [];
      let query = event.query;
  
      for (let i = 0; i < (this.options as any[]).length; i++) {
        let option = (this.options as any[])[i];
        if (option.label.toLowerCase().indexOf(query.toLowerCase()) == 0) {
          filtered.push(option);
        }
      }
  
      this.model.fieldOptions = filtered;
    }
  }

  onDropdownClick(auto: any) {
    if (this.emailVerified) {
      // this.updateOptions();
      this.model.fieldOptions = this.options; 
      auto.show();
    }
  }

  needToShowOtherCollegeDetails() {
    const answer = this.model.answer[0];
    let isOthers = false;

    if(typeof answer === "string" && answer.toLowerCase() === "others") {
      isOthers = true;
    }

    if(typeof answer === "object" && answer?.value?.toLowerCase() === "others") {
      isOthers = true;
    }

    return ["pg_college_name", "ug_college_name", "diploma_college_name", "pg_degree", "ug_degree", "diploma_degree", "pg_specialization", "ug_specialization", "diploma_specialization"].includes(this.model.fieldValue) && isOthers
  }

  institutionsPage: number = 1;
  hasMoreInstitutions: boolean = true;
  loadingInstitutions: boolean = false;
  institutionsSearchQuery: string = "";

  onDropdownShow(event: any) {
    if (!["pg_college_name", "ug_college_name", "diploma_college_name"].includes(this.model.fieldValue)) {
      return;
    }

    setTimeout(() => {
      const dropdownPanels = document.querySelectorAll('.p-autocomplete-list-container');
      dropdownPanels.forEach(panel => {
        panel.addEventListener('scroll', this.handleDropdownScroll.bind(this));
      });
    }, 200);
  }

  onDropdownHide(event: any) {
    if (!["pg_college_name", "ug_college_name", "diploma_college_name"].includes(this.model.fieldValue)) {
      return;
    }
  
    const dropdownPanels = document.querySelectorAll('.p-autocomplete-list-container');
    dropdownPanels.forEach(panel => {
      panel.removeEventListener('scroll', this.handleDropdownScroll.bind(this));
    });
  }

  handleDropdownScroll(event: any) {
    const element = event.target;
    const threshold = 100;

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      if (this.hasMoreInstitutions && !this.loadingInstitutions) {
          setTimeout(() => {
              if (this.hasMoreInstitutions && !this.loadingInstitutions) {
                this.institutionsPage++;
                this.getInstitutions();
              }
          }, 300);
      }
    }
  }

  getInstitutions() {
    this.loadingInstitutions = true;
    let search = this.institutionsSearchQuery;
    if(search && search.includes(",")) {
      search = search.split(",")[0];
    }

    const params: any =  { page: this.institutionsPage, s: search };
    if(!search) {
      delete params.s;
    }

    this.institutionsSearch$.next();
    this.formBuilderService.getEventSpecificFormDetailsFastApi(this.eventId, { includeInstitutes: true }, 'form', params).pipe(takeUntil(this.institutionsSearch$)).subscribe((res) => {
      const institutions = res.body.institutes;
      const items = institutions.items || [];

      if (items) {
        if (items.length) {
          (items || []).forEach((institution: any) => {
              if(typeof institution.name === "string" && institution.name?.toLowerCase() === "others") {
                  institution.city = "";
                  institution.state = "";
              }
          });
          const updatedItems = this.formBuilderService.mapFieldOptions('institute', items, ['name', 'city', 'state'], ['name', 'city', 'state'])
          this.model.fieldOptions = [...this.model.fieldOptions, ...updatedItems];
          this.updateOptions();
        }

        if (items.length < institutions.size) {
          this.hasMoreInstitutions = false;
        }
      } else {
        this.hasMoreInstitutions = false;
      }

      this.loadingInstitutions = false;
    }, (err) => {
      this.loadingInstitutions = false;
      this.hasMoreInstitutions = true;
    })
  }
}